// services/AdminService.js

import { base_url } from "../utils/constante";



// 🔑 Helper : récupère le token admin stocké
function getToken() {
    return localStorage.getItem("token_admin") || sessionStorage.getItem("token_admin");
}

// 🔑 Helper : headers JSON + auth
function authHeaders() {
    return {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
    };
}

// ══════════════════════════════════════════════════════════
//  AUTH ADMIN
// ══════════════════════════════════════════════════════════

export async function loginAdmin(email, password) {
    const response = await fetch(`${base_url}login/admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
        throw new Error(data.message || "Connexion admin échouée");
    }
    return data; // { success, token, user }
}

// ══════════════════════════════════════════════════════════
//  TICKETS
// ══════════════════════════════════════════════════════════

// 📋 Liste complète (admin) — tickets + annonces (event/publicite)
export async function getAdminTickets() {
    const response = await fetch(`${base_url}admin/tickets`, {
        method: "GET",
        headers: authHeaders(),
    });

    if (!response.ok) throw new Error("Erreur lors de la récupération des tickets");
    return await response.json();
}

// 🔍 Un ticket précis
export async function getTicketById(id) {
    const response = await fetch(`${base_url}admin/tickets/${id}`, {
        method: "GET",
        headers: authHeaders(),
    });
    if (!response.ok) throw new Error("Ticket introuvable");
    return await response.json();
}

// ➕ Créer un ticket
export async function createTicket({ titre, isDisabled = false, position = null }) {
    const body = { titre, isDisabled };
    if (position !== null) body.position = position;

    const response = await fetch(`${base_url}admin/tickets`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Erreur création ticket");
    return data;
}

// ✏️ Mettre à jour un ticket
export async function updateTicket(id, fields) {
    const response = await fetch(`${base_url}admin/tickets/${id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(fields),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Erreur mise à jour ticket");
    return data;
}

// 🚫 Activer / désactiver (toggle)
export async function toggleTicket(id) {
    const response = await fetch(`${base_url}admin/tickets/${id}/toggle`, {
        method: "PATCH",
        headers: authHeaders(),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Erreur désactivation ticket");
    return data; // { message, isDisabled }
}

// 🗑️ Supprimer un ticket
export async function deleteTicket(id) {
    const response = await fetch(`${base_url}admin/tickets/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Erreur suppression ticket");
    return data;
}

// ══════════════════════════════════════════════════════════
//  ANNONCES
// ══════════════════════════════════════════════════════════

// ➕ Ajouter une annonce à un ticket
// - type "event"     → JSON  : { ticket_id, type:"event", event_id }
// - type "publicite" → FormData (avec fichier image) : ticket_id, type, titre, image(File), events[]
export async function createAnnonce(payload) {
    const isForm = payload instanceof FormData;

    // En multipart, NE PAS fixer Content-Type (le navigateur ajoute le boundary)
    const headers = isForm
        ? { Accept: "application/json", Authorization: `Bearer ${getToken()}` }
        : authHeaders();

    const response = await fetch(`${base_url}admin/annonces`, {
        method: "POST",
        headers,
        body: isForm ? payload : JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
        const msg = data.message || (data.errors ? JSON.stringify(data.errors) : "Erreur création annonce");
        throw new Error(msg);
    }
    return data;
}

// 🗑️ Supprimer une annonce
export async function deleteAnnonce(id) {
    const response = await fetch(`${base_url}admin/annonces/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Erreur suppression annonce");
    return data;
}

// ✏️ Modifier une publicité (titre + liste d'events + IMAGE optionnelle)
// fields = { titre, events: [id, id, ...], image?: File }
//
// ⚠️ On envoie un FormData en POST avec _method=PUT (method spoofing) :
//    Laravel route ça vers la méthode PUT existante, et PHP ne parse
//    les fichiers (multipart) qu'en POST — impossible en vrai PUT.
export async function updatePublicite(annonceId, fields) {
    const fd = new FormData();
    fd.append("_method", "PUT"); // 🔑 method spoofing Laravel

    // titre : on ne l'ajoute que s'il est renseigné (sinon le backend garde l'ancien)
    if (fields.titre !== undefined && fields.titre !== null && fields.titre !== "") {
        fd.append("titre", fields.titre);
    }

    // events[] (au moins 1 requis côté backend)
    (fields.events || []).forEach((id) => fd.append("events[]", id));

    // image : uniquement si un NOUVEAU fichier a été choisi
    if (fields.image instanceof File) {
        fd.append("image", fields.image);
    }

    const response = await fetch(`${base_url}admin/annonces/${annonceId}/publicite`, {
        method: "POST", // ← POST + _method=PUT (pas "PUT")
        headers: {
            // ⚠️ Pas de Content-Type ici : le navigateur ajoute le boundary
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
        body: fd,
    });

    const data = await response.json();
    if (!response.ok) {
        const msg = data.message || (data.errors ? JSON.stringify(data.errors) : "Erreur mise à jour publicité");
        throw new Error(msg);
    }
    return data;
}

// ══════════════════════════════════════════════════════════
//  RECHERCHE EVENTS (autocomplétion admin)
// ══════════════════════════════════════════════════════════

// 🔍 Recherche d'événements par nom (max 10 résultats, événements à venir)
export async function searchEvents(q) {
    const response = await fetch(
        `${base_url}admin/events/search?q=${encodeURIComponent(q)}`,
        { method: "GET", headers: authHeaders() }
    );

    if (!response.ok) throw new Error("Erreur lors de la recherche d'événements");
    return await response.json(); // { success, events: [...] }
}